import { DeleteIcon } from '@carboncopyco/icons';
import {
  type TourFrame,
  type TourStep,
  type TourStepConfigUpdateData,
  type TourStepConfigUpdateRequest,
  configsSpecificToTooltip,
  type onAddStepProps,
} from '@carboncopyco/types';
import {
  AnalyticsTrackEventName,
  camelCaseToUnderscore,
  getKeysWithDifferentValues,
  isDesktop,
  useAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { get, isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  selectOrganizationId,
  useDiscardFramesDraftsMutation,
  useGetCurrentUserQuery,
  useGetTourFramesDraftQuery,
  useGetTourQuery,
  useUpdateAllTourStepsMutation,
  useUpdateTourFrameStepMutation,
  useUpdateTourFrameStepsMutation,
} from '@carboncopyco/data';
import { ConfirmAlert, notify } from '@carboncopyco/ui';
import { scopedLogger, Scope } from '@carboncopyco/logger';
import { usePlayableFrames } from '../../hooks/replayer';
import { useTourDispatch, useTourSelector } from '../../hooks/store';
import { usePageEvent } from '../../hooks/usePageEvent';
import { useTourEvents } from '../../hooks/useTourEvents';
import PageLayout from '../../layouts/PageLayout';
import {
  selectActiveFrameId,
  selectAddingStepInProgress,
  selectCapturedScreenSize,
  selectDraftSettings,
  selectFocusFrameIndex,
  selectIsApplyingTourTheme,
  selectIsScreenshotAsFrameUploading,
  selectIsSettingsModified,
  setDraftSettings,
  setFocusFrameIndex,
  setIsApplyingTourTheme,
  setIsMediaUploading,
  setIsScreenshotAsFrameUploading,
} from '../../store';
import { getTourBackgroundStyles } from '../../utils';
import { EDITOR_DRAWER_WIDTH } from '../../utils/constants';
import { TourEditorNavbar } from './TourEditorNavbar';
import {
  type TourEditorPageProps,
  TourEditorPageProvider,
} from './TourEditorPageContext';
import {
  type ReplayerEditorRef,
  ToursReplayerEditor,
} from './ToursReplayerEditor';
import { Drawer } from './components/Drawer';
import useAddStepActions from './components/FrameList/Frame/AddStep/useAddStepActions';
import { getUpdatedSteps } from './helpers';
import useEditorTrackTooltipEvents from './useEditorTrackTooltipEvents';

const sdkLogger = scopedLogger(Scope.MIMESIS_SDK);

export const TourEditorPage = ({
  useNavigate,
  mimesisStepEngineUrl,
  fileServiceDynamicLink,
  isDebugMode,
  deeplinkProtocol,
  tabId,
  activeTabId,
  rootElement,
  uuid,
  isDiscardModalOpen,
  onUpdateEditorTabVisibility,
  setIsDiscardModalOpen,
  onIsTourModifiedChange,
  onExit,
}: TourEditorPageProps) => {
  const organizationId = useSelector(selectOrganizationId);

  const { trackEvent } = useAnalytics();
  const replayerRef = useRef<ReplayerEditorRef>(null);
  const stepConfigUpdatesData = useRef<TourStepConfigUpdateData>({
    stepId: null,
    updates: [],
  });
  const hasStepConfigUpdates = useRef(false);
  const dispatch = useTourDispatch();
  const { tourId, workspaceId } = useParams<{
    tourId: string;
    workspaceId: string;
  }>();
  const { navigateToTourPageByWorkspace } = useNavigate();
  const activeFrameId = useTourSelector(selectActiveFrameId(uuid));
  const capturedScreenSize = useTourSelector(selectCapturedScreenSize(uuid));
  const [isUpdatingAllSteps, setIsUpdatingAllSteps] = useBoolean(false);
  const [isPublishInitiated, setIsPublishInitiated] = useBoolean(false);
  const {
    data: { user },
  } = useGetCurrentUserQuery();
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(0);
  const [updateTourSteps] = useUpdateTourFrameStepsMutation();
  const [updateTourStep] = useUpdateTourFrameStepMutation();
  const [updateAllTourSteps] = useUpdateAllTourStepsMutation();
  const [discardFramesDrafts, { isLoading: isDiscardLoading }] =
    useDiscardFramesDraftsMutation();
  const { data: tour } = useGetTourQuery(
    { organizationId, workspaceId, id: Number(tourId) },
    { skip: !tourId, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!tour?.draftSettings) {
      return;
    }

    dispatch(
      setDraftSettings({
        uuid,
        draftSettings: tour.draftSettings,
      })
    );
  }, [tour?.draftSettings]);

  const draftSettings = useTourSelector(selectDraftSettings(uuid));

  const {
    data: frames,
    refetch: refetchFrames,
    isFetching: isFetchingFrames,
  } = useGetTourFramesDraftQuery(
    {
      organizationId,
      id: Number(tourId),
    },
    { skip: !tourId }
  );
  const { tourEvents } = useTourEvents(tour, frames, uuid);
  const playableFrames = usePlayableFrames(uuid, frames, true);

  const { handleTooltipTrackEvents } = useEditorTrackTooltipEvents({
    tourId,
    activeFrameId,
  });

  const focusFrameIndex = useTourSelector(selectFocusFrameIndex(uuid));

  const isScreenshotAsFrameUploading = useTourSelector(
    selectIsScreenshotAsFrameUploading(uuid)
  );

  const { cancelAddingStep } = useAddStepActions({
    uuid,
    frameId: activeFrameId,
    tourId: Number(tourId),
    replayerRef: replayerRef,
  });

  useEffect(() => {
    if (focusFrameIndex !== null) {
      selectFrameByIndex(focusFrameIndex);
      dispatch(
        setFocusFrameIndex({
          uuid,
          frameIndex: null,
        })
      );

      if (!isScreenshotAsFrameUploading) {
        return;
      }

      // small delay to load image first and avoid flickering
      setTimeout(() => {
        dispatch(
          setIsScreenshotAsFrameUploading({
            uuid,
            isUploading: false,
          })
        );
      }, 1000);
    }
  }, [frames]);

  useEffect(() => {
    const frameIndex = frames?.findIndex((frame) => frame.id === activeFrameId);
    if (frameIndex !== -1) {
      setSelectedFrameIndex(frameIndex);
    }
  }, [activeFrameId, frames]);

  usePageEvent();

  const isFrameModified = frames?.some((frame) => frame.modified);
  const isSettingsModified = useTourSelector(selectIsSettingsModified(uuid));

  const isTourModified = isFrameModified || isSettingsModified;

  useEffect(() => {
    onIsTourModifiedChange?.(isTourModified);
  }, [isTourModified]);

  const activeFrame = useRef<TourFrame>();
  activeFrame.current = frames?.find(({ id }) => activeFrameId === id);

  const isApplyingTourTheme = useTourSelector(selectIsApplyingTourTheme(uuid));
  const lastFrames = useRef(frames);
  useEffect(() => {
    const areFramesChanged = frames && !isEqual(lastFrames.current, frames);
    if (areFramesChanged) {
      lastFrames.current = frames;
      if (isApplyingTourTheme) {
        replayerRef.current?.refreshTooltip();
        dispatch(
          setIsApplyingTourTheme({
            uuid,
            isApplyingTourTheme: false,
          })
        );
      }
    }
  }, [frames, isApplyingTourTheme, dispatch, uuid]);

  const handleOnEditStep = ({
    step,
    frameId,
    onSuccess,
  }: {
    step: TourStep;
    frameId: TourFrame['id'];
    onSuccess?: () => void;
  }) => {
    if (!(step && frameId)) {
      return;
    }
    updateTourStep({
      organizationId,
      tourId: Number(tourId),
      frameId,
      step,
    })
      .unwrap()
      .then(() => {
        onSuccess?.();
      })
      .catch(() => notify.error('Failed to save step changes'))
      .finally(() => {
        dispatch(
          setIsMediaUploading({
            uuid,
            isMediaUploading: false,
          })
        );
      });
  };

  const handleOnUpdateAllSteps = async (onSuccess?: () => void) => {
    setIsUpdatingAllSteps.setTrue();
    updateAllTourSteps({
      organizationId,
      tourId: Number(tourId),
      updates: stepConfigUpdatesData.current.updates,
    })
      .unwrap()
      .then(() => {
        resetStepConfigUpdatesData();
        onSuccess?.();
        notify.success('Tour tooltip style applied successfully');
      })
      .catch(() => notify.error('Failed to update all steps'))
      .finally(() => {
        setIsUpdatingAllSteps.setFalse();
      });

    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_TOOLTIP_APPLY_TO_ALL, {
      location: 'tour_editor_tooltip',
      tourId: tourId,
    });
  };

  const handleOnAddStep = ({
    step,
    referenceStepId,
    direction,
  }: onAddStepProps) => {
    const steps = getUpdatedSteps({
      step,
      referenceStepId,
      direction,
      frames,
      activeFrame,
    });
    sdkLogger.info('handleOnAddStep', { activeFrame, steps });
    updateTourSteps({
      organizationId,
      tourId: Number(tourId),
      frameId: activeFrame.current?.id,
      steps,
    })
      .unwrap()
      .then(() => {
        cancelAddingStep(step.stepId);
      })
      .catch((e) => {
        notify.error('Failed to add the new step');
        sdkLogger.error(
          'Failed to add the new step',
          { activeFrame, step, steps, referenceStepId, direction },
          e
        );
      });
  };

  const handleOnStepConfigUpdate = (
    updateRequest: TourStepConfigUpdateRequest
  ) => {
    let { updates: currentUpdates } = stepConfigUpdatesData.current;
    const { stepId: lastStepId } = stepConfigUpdatesData.current;
    const { stepId, newTooltipConfig, prevTooltipConfig } = updateRequest;
    const { keys } = getKeysWithDifferentValues(
      newTooltipConfig,
      prevTooltipConfig
    );
    if (lastStepId !== stepId) {
      currentUpdates = [];
    }
    let newUpdates = [...currentUpdates];

    const keysToUpdate = getConfigKeysToUpdate(keys);
    keysToUpdate.forEach((key) => {
      const newPath = `config.tooltip.${camelCaseToUnderscore(key)}`;
      newUpdates = newUpdates.filter((update) => update.path !== newPath);

      // When applying to all, we need to align styles for next and last step buttons
      if (key.startsWith('buttons.nextButton')) {
        newUpdates.push({
          path: `config.tooltip.${camelCaseToUnderscore(
            key.replace('buttons.nextButton', 'buttons.lastStepButton')
          )}`,
          value: get(newTooltipConfig, key),
        });
      }

      if (key.startsWith('buttons.lastStepButton')) {
        newUpdates.push({
          path: `config.tooltip.${camelCaseToUnderscore(
            key.replace('buttons.lastStepButton', 'buttons.nextButton')
          )}`,
          value: get(newTooltipConfig, key),
        });
      }

      newUpdates.push({
        path: newPath,
        value: get(newTooltipConfig, key),
      });
    });
    stepConfigUpdatesData.current = {
      stepId,
      updates: newUpdates,
    };
    if (newUpdates.length > 0) {
      hasStepConfigUpdates.current = true;
    }
    handleTooltipTrackEvents(stepId, keys, newTooltipConfig);
  };

  const getConfigKeysToUpdate = (keysWithDifferentValues: string[]) => {
    const keys: string[] = [];
    keysWithDifferentValues.forEach((key) => {
      const isAlreadyAdded = keys.find((currentKey) => currentKey === key);
      if (isAlreadyAdded) {
        return;
      }

      if (
        key.includes('spotlightOptions') ||
        key.includes('highlightElementOptions')
      ) {
        keys.push(key.split('.')[0]);
        return;
      }
      if (key.includes('stepCount')) {
        keys.push('text.stepCount');
        return;
      }

      if (key.endsWith('.r') || key.endsWith('.g') || key.endsWith('.b')) {
        const keyBaseForColor = key.substring(0, key.length - 1);
        keys.push(`${keyBaseForColor}r`);
        keys.push(`${keyBaseForColor}g`);
        keys.push(`${keyBaseForColor}b`);
        return;
      }

      const isKeyToOmit = configsSpecificToTooltip.find(
        (currentKey) => currentKey === key
      );
      !isKeyToOmit && keys.push(key);
    });

    return keys;
  };

  const handleOnStepChange = (stepId: string) => {
    if (stepId !== stepConfigUpdatesData.current.stepId) {
      resetStepConfigUpdatesData();
    }
  };

  const resetStepConfigUpdatesData = () => {
    stepConfigUpdatesData.current = {
      stepId: null,
      updates: [],
    };
    hasStepConfigUpdates.current = false;
  };

  const handleConfirmDiscard = () => {
    discardFramesDrafts({
      organizationId,
      id: Number(tourId),
    })
      .unwrap()
      .then(() => {
        setIsDiscardModalOpen(false);

        if (isDesktop()) {
          onExit?.();
          return;
        }
        navigateToTourPageByWorkspace(tourId, workspaceId, 'overview');
      })
      .catch(() => {
        notify.error('Sorry, something went wrong');
        setIsDiscardModalOpen(false);
      });

    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_EDITOR_DISCARD_AND_EXIT, {
      location: 'tour_editor_navbar',
      tourId,
    });
  };

  useEffect(() => {
    if (playableFrames && !isFetchingFrames && isPublishInitiated) {
      setIsPublishInitiated.setFalse();
      replayerRef.current?.playFrame(playableFrames[0]);
    }
  }, [playableFrames, isFetchingFrames, isPublishInitiated]);

  const findFirstNonDeletedFrame = (frameIndex: number) => {
    // first search for first non deleted frame before frameIndex
    let frame = (
      frameIndex !== 0 ? frames.slice(0, frameIndex).reverse() : frames
    ).find((frame) => !frame.isDisabled && frame.isActive);

    if (frame === undefined) {
      // if there are no non deleted frames before frameIndex, search for frames after frameIndex
      frame = frames
        .slice(frameIndex, frames.length)
        .find((frame) => !frame.isDisabled && frame.isActive);
    }

    return frame;
  };

  const selectFrameByIndex = (frameIndex: number) => {
    if (!frames) {
      return;
    }

    const frame = frames[frameIndex];

    /**
     * When focusing frames, we don't want to focus deleted frames since they are not displayed
     * For frameIndex 0 we want to focus first not deleted frame
     * For frameIndex !== 0 we want to focus last not deleted frame before frameIndex
     *
     * Note: isDisabled means that frame is deleted. Don't mix isDisabled(delete functionality) and isActive(show/hide)
     */
    const frameToPlay = frame.isDisabled
      ? findFirstNonDeletedFrame(frameIndex)
      : frame;

    replayerRef?.current?.playFrame(frameToPlay);
  };

  const isSomeFrameWithNoSteps = useMemo(
    () =>
      !!frames?.some(
        (frame) =>
          frame.steps.length === 0 && frame.isActive && !frame.isDisabled
      ),
    [frames]
  );

  const doReplay = (step: TourStep, _frameId: number) => {
    replayerRef?.current?.playFrameByStepId(step.stepId);
  };

  const isAddingStepInProgress = useSelector(selectAddingStepInProgress(uuid));

  const handleDrawerAndNavbarClick = () => {
    // Clicking anywhere in the drawer should abort adding step action
    if (isAddingStepInProgress) {
      cancelAddingStep();
    }
  };

  return (
    <TourEditorPageProvider
      useNavigate={useNavigate}
      mimesisStepEngineUrl={mimesisStepEngineUrl}
      fileServiceDynamicLink={fileServiceDynamicLink}
      isDebugMode={isDebugMode}
      deeplinkProtocol={deeplinkProtocol}
      uuid={uuid}
      onUpdateEditorTabVisibility={onUpdateEditorTabVisibility}
      rootElement={rootElement}
    >
      <PageLayout
        navbar={
          <TourEditorNavbar
            tour={tour}
            entityName={tour?.tourName}
            isSomeFrameWithNoSteps={isSomeFrameWithNoSteps}
            useNavigate={useNavigate}
            dimension={{
              width:
                draftSettings?.screenResolution?.width ||
                capturedScreenSize?.width,
              height:
                draftSettings?.screenResolution?.height ||
                capturedScreenSize?.height,
              type: draftSettings?.screenResolution?.type || 'responsive',
            }}
            onDiscard={() => {
              if (isTourModified) {
                setIsDiscardModalOpen(true);
                return;
              }
              navigateToTourPageByWorkspace(tourId, workspaceId, 'overview');
            }}
            isTourModified={isTourModified}
            onPublishTour={() => {
              setIsPublishInitiated.setTrue();
            }}
            isPublishInProgress={isPublishInitiated}
            onNavbarClick={handleDrawerAndNavbarClick}
            refetchFrames={refetchFrames}
            draftSettings={draftSettings}
            onPublishAndExit={onExit}
          />
        }
      >
        <div className="flex flex-col h-full">
          <div className="h-full flex overflow-hidden">
            <div
              className="border-r border-ds-natural-150"
              style={{
                width: `${EDITOR_DRAWER_WIDTH}px`,
                minWidth: `${EDITOR_DRAWER_WIDTH}px`,
              }}
            >
              <Drawer
                workspaceId={workspaceId}
                frames={frames}
                user={user}
                tourId={tourId}
                tour={tour}
                selectedFrameIndex={selectedFrameIndex}
                replayerRef={replayerRef}
                doReplay={doReplay}
                demoIdsTourIsBasedOn={tour?.demoData?.map(
                  (data) => data.demoId
                )}
                useNavigate={useNavigate}
                deeplinkProtocol={deeplinkProtocol}
                onDrawerClick={handleDrawerAndNavbarClick}
              />
            </div>
            <div
              className={`h-full ${draftSettings?.background?.image ? 'bg-cover' : 'bg-repeat'}`}
              style={{
                width: `calc(100vw - ${EDITOR_DRAWER_WIDTH}px`,
                ...getTourBackgroundStyles({
                  tourSettings: draftSettings,
                  isEditor: true,
                  fileServiceApiToken: tour?.fileServiceToken,
                }),
              }}
            >
              {draftSettings?.background?.blur && (
                <div className="backdrop-blur-xl absolute w-full h-full" />
              )}
              {tour && frames?.length && (
                <ToursReplayerEditor
                  workspaceId={workspaceId}
                  ref={replayerRef}
                  frames={frames || []}
                  events={tourEvents || []}
                  draftSettings={draftSettings || tour.draftSettings}
                  fileServiceApiToken={tour?.fileServiceToken}
                  organizationId={organizationId}
                  tourId={tourId}
                  onAddStep={handleOnAddStep}
                  onEditStep={handleOnEditStep}
                  onUpdateAllSteps={handleOnUpdateAllSteps}
                  onStepConfigUpdate={handleOnStepConfigUpdate}
                  isUpdatingAllSteps={isUpdatingAllSteps}
                  hasStepConfigUpdates={hasStepConfigUpdates}
                  onStepChange={handleOnStepChange}
                  selectFrameByIndex={selectFrameByIndex}
                  tabId={tabId}
                  activeTabId={activeTabId}
                  rootElement={rootElement}
                />
              )}
            </div>
            <ConfirmAlert
              loading={isDiscardLoading}
              containerClass="w-ds-500"
              show={isDiscardModalOpen}
              onConfirm={handleConfirmDiscard}
              onClose={() => setIsDiscardModalOpen(false)}
              title="Discard changes"
              description="Discard your progress and exit the tour editor?"
              yesLabel="Discard and exit"
              noLabel="Cancel"
              danger
              yesIcon={<DeleteIcon />}
            />
          </div>
        </div>
      </PageLayout>
    </TourEditorPageProvider>
  );
};
